<script setup lang="ts">
  import type { TCountryType } from '~/types'
  import { vMaska } from 'maska/vue'
  import { ref, watch, computed, onMounted } from 'vue'
  import { countryFormats as defaultCountryFormats } from '~/helpers/countryFormats'

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
  }>()

  const props = withDefaults(defineProps<{
    placeholder?: string
    countryFormats?: Record<string, TCountryType>
    modelValue: string
    error?: string
    label?: string
    disabled?: boolean,
    ghost?: boolean,
  }>(), {
    countryFormats: () => defaultCountryFormats,
  })

  const formatInitialPhone = (value: string) => {
    if (!value) return ''

    if (/[^0-9+]/.test(value)) return value

    const digitsOnly = value.replace(/\D/g, '')
    const countries = Object.values(props.countryFormats).sort(
      (a, b) => b.code.length - a.code.length
    )
    const country = countries.find((c) =>
      digitsOnly.startsWith(c.code.replace(/\D/g, ''))
    )

    if (!country) return value.startsWith('+') ? value : `+${value}`

    let result = country.code
    let digitIndex = 0
    const digits = value.slice(country.code.replace(/\D/g, '').length)
    for (
      let i = country.code.length;
      i < country.mask.length && digitIndex < digits.length;
      i++
    ) {
      result += country.mask[i] === '#' ? digits[digitIndex++] : country.mask[i]
    }
    return result
  }

  const formattedPhone = computed(() => formatInitialPhone(props.modelValue))

  const phone = ref('')
  const currentMask = ref('')
  const currentCountry = ref<TCountryType | undefined>()
  const isInvalidCountry = ref(false)

  const sortedCountries = computed(() =>
    Object?.values(props?.countryFormats).sort(
      (a, b) => b?.code.length - a?.code.length
    )
  )

  const applyMask = (code: string, mask: string, digits: string) => {
    let result = code
    let digitIndex = 0

    for (
      let i = code.length;
      i < mask.length && digitIndex < digits.length;
      i++
    ) {
      result += mask[i] === '#' ? digits[digitIndex++] : mask[i]
    }

    return result
  }

  const displayValue = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  })

  onMounted(() => {
    if (!props.modelValue) return
    const formatted = formatInitialPhone(props.modelValue)
    displayValue.value = formatted
    phone.value = formatted
    handleInput()
  })

  const detectCountry = (value: string): TCountryType | undefined => {
    if (!value.startsWith('+')) return undefined
    const digitsOnly = value.replace(/\D/g, '')
    return sortedCountries.value.find((country) =>
      digitsOnly.startsWith(country.code.replace(/\D/g, ''))
    )
  }

  const refElement = ref()

  const handleInput = (e?: Event) => {
    const target = (e?.target as HTMLInputElement)
      ? e?.target
      : refElement.value?.inputRef
    if (!target) return
    let value = target.value.replace(/[^0-9+]/g, '')

    if (value && !value.startsWith('+')) {
      value = `+${value}`
    }

    phone.value = value
  }

  const resetPhone = () => {
    phone.value = ''
    displayValue.value = ''
    currentMask.value = ''
    currentCountry.value = undefined
    isInvalidCountry.value = false
  }

  watch(phone, (value) => {
    if (!value) {
      resetPhone()
      return
    }

    const country = detectCountry(value)
    currentCountry.value = country
    currentMask.value = country?.mask || ''
    isInvalidCountry.value = !country && value.length > 4

    if (isInvalidCountry.value) return

    displayValue.value = country
      ? applyMask(
          country.code,
          country.mask,
          value.slice(country.code.length).replace(/\D/g, '')
        )
      : value
  })

  watch(
    () => props.modelValue,
    (newVal) => {
      if (newVal && newVal !== displayValue.value) {
        const formatted = formatInitialPhone(newVal)
        displayValue.value = formatted
        phone.value = formatted
      }
    }
  )

  const handleKeyDown = (e: KeyboardEvent) => {
    if (
      isInvalidCountry.value &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      e.preventDefault()
    }
  }
</script>

<template>
  <template v-if="ghost">
    <span>{{ formattedPhone }}</span>
  </template>
  <div v-else class="flex flex-col gap-1">
    <label v-if="label" class="text-sm">
      {{ label }}
    </label>

    <UInput
      v-model="displayValue"
      v-maska="currentMask"
      ref="refElement"
      :placeholder="currentCountry?.mask || placeholder"
      icon="i-heroicons-phone"
      :ui="{
        base: `py-2.5 !text-black/80 dark:!text-white/80 ${
          error ? 'ring-2 !ring-red-500' : ''
        }`,
      }"
      @input="handleInput"
      :disabled="disabled"
      @keydown="handleKeyDown"
      @keypress="
        (e: KeyboardEvent) => /[0-9+]/.test(e.key) || e.preventDefault()
      "
    >
      <template #trailing>
        <UIcon
          v-if="currentCountry?.icon"
          :name="currentCountry?.icon"
          class="ml-2 h-5 w-5"
        />
      </template>
    </UInput>

    <p v-if="error" class="text-red-600 dark:text-red-500">
      {{ error }}
    </p>
  </div>
</template>
